import {
  AfterViewInit,
  Component,
  ErrorHandler,
  OnInit,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  UntypedFormControl,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { isSentryErrorHandler } from '@techniek-team/sentry-web';
import {
  BehaviorSubject,
  firstValueFrom,
  Observable,
  ReplaySubject,
} from 'rxjs';
import { delay, filter, map } from 'rxjs/operators';
import { AlfredClientConfig } from '../../../../alfred-client.config';
import { AlfredClientService } from '../../../../alfred-client.service';
import { ScaleQuestionAnswer } from '../../../../models/answer/scale-answer.model';
import { IntRange } from '../../../../models/int-range/int-range';
import { SliderQuestion } from '../../../../models/questions/slider-question.model';
import { OnLoaded } from '@lyceo/shared/dynamic-component-loading/feature-main';
import { InvalidAnswerTypeError } from '../../../../shared/errors/invalid-answer-type-error';
import { outOfRangeValidator } from '../../../../shared/validators/out-of-range.validator';
import { QuestionControlType } from '../question-type.interface';
import { QuestionDisplayComponent } from '../../question-display/question-display.component';
import { IonRange, IonLabel } from '@ionic/angular/standalone';
import { AsyncPipe } from '@angular/common';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'lib-slider-question',
  templateUrl: './slider-question.component.html',
  styleUrls: ['./slider-question.component.scss'],
  imports: [
    QuestionDisplayComponent,
    IonRange,
    ReactiveFormsModule,
    IonLabel,
    AsyncPipe,
  ],
})
export class SliderQuestionComponent
  implements OnInit, AfterViewInit, OnLoaded, QuestionControlType
{
  private readonly errorHandler: ErrorHandler = inject(ErrorHandler);
  private readonly config: AlfredClientConfig = inject(AlfredClientConfig);
  private readonly alfredClientService: AlfredClientService =
    inject(AlfredClientService);

  /**
   * questions data used to created the gap match question.
   * private Used in inner html template.
   */
  public _question$: BehaviorSubject<SliderQuestion> =
    new BehaviorSubject<SliderQuestion>(undefined as never);

  /**
   * Map the incoming IntRangeModel to an usable IntRange class
   */
  public set question(question: SliderQuestion) {
    if (!question.scaleRange && this.alfredClientService.isDirectHandleMode()) {
      question.scaleRange = new IntRange(0, 10, true, true);
    }

    if (
      !question.scaleQuestionLabels &&
      this.alfredClientService.isDirectHandleMode()
    ) {
      question.scaleQuestionLabels = [];
    }
    if (Array.isArray(question.scaleQuestionLabels)) {
      question.scaleQuestionLabels = question.scaleQuestionLabels.sort(
        (a, b) => a.onScalePoint - b.onScalePoint,
      );
    }

    this._question$.next(question);
  }

  /**
   * Input to prefill the answer of the question.
   */
  public set answer(answer: ScaleQuestionAnswer) {
    if (!answer) {
      this.reset();
      return;
    }

    if (typeof answer === 'number') {
      this.rangeCtrl.setValue(answer);
      return;
    }

    if (this.alfredClientService.isDirectHandleMode()) {
      return;
    }

    // This form control only accepts an object (array) or null.
    // Other enums are ignored and a silent error is send to Sentry
    if (!this.config.production) {
      throw new InvalidAnswerTypeError(answer, 'number');
    }

    if (isSentryErrorHandler(this.errorHandler)) {
      const error: InvalidAnswerTypeError = new InvalidAnswerTypeError(
        answer,
        'number',
      );
      this.errorHandler.captureError(error.message);
    }
  }

  public get answer(): ScaleQuestionAnswer {
    return this.getValue() as ScaleQuestionAnswer;
  }

  /**
   * The form control ion-range component
   */
  public rangeCtrl: UntypedFormControl = new UntypedFormControl(null, [
    Validators.required,
  ]);

  /**
   * Subject which starts to emit when the view is rendered. This is needed because the
   * component is dynamically loading.
   */
  public onFinishedInit$: ReplaySubject<void> = new ReplaySubject<void>();

  /**
   * @inheritDoc
   */
  public ngOnInit(): void {
    // When viewing an already answered slider question that has an answer we
    // should see that answer on the slider and not the default value that
    // is set by the reset().
    if (!this.answer) {
      this.reset();
    }

    this.rangeCtrl.setValidators([
      Validators.required,
      outOfRangeValidator(this._question$.getValue().scaleRange),
    ]);
  }

  /**
   * @inheritDoc
   */
  public ngAfterViewInit(): void {
    this.onFinishedInit$.next();
    this.onFinishedInit$.complete();
  }

  /**
   * @inheritDoc
   */
  public ready(): Promise<void> {
    return firstValueFrom(this.onFinishedInit$.pipe(delay(20)));
  }

  /**
   * @inheritDoc
   */
  public isPristine(): boolean {
    return this.rangeCtrl.pristine;
  }

  /**
   * @inheritDoc
   */
  public getValue(): ScaleQuestionAnswer {
    return this.rangeCtrl.value;
  }

  /**
   * @inheritDoc
   */
  public onValueChanges(): Observable<ScaleQuestionAnswer> {
    // skipping the initial value of the behavior u
    return this.rangeCtrl.valueChanges;
  }

  /**
   * @inheritDoc
   */
  public isValid(): boolean {
    return this.rangeCtrl.valid;
  }

  /**
   * @inheritDoc
   */
  public onValidChanges(): Observable<boolean> {
    return this.rangeCtrl.statusChanges.pipe(
      filter((status) => status === 'VALID' || status === 'INVALID'),
      map((status) => status === 'VALID'),
    );
  }

  public setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.rangeCtrl.disable();
    } else {
      this.rangeCtrl.enable();
    }
  }

  /**
   * Position the knob of the slider by default in the center of the bar.
   */
  public reset(): void {
    this.rangeCtrl.setValue(
      Math.round(this._question$.getValue().scaleRange.max / 2),
    );
    this.rangeCtrl.markAsPristine();
  }
}
